import { Component, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { dlog } from '@/utils/devlog';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';

/*
  Verifies the user invite and redirects based on the password flag.
  Checks user.user_metadata.requires_password: true → /set-password, false → /dashboard.
  Handles OTP expired errors by redirecting to /login with a toast message.
  Prevents blank page with error boundary, timeout, and session refresh.
*/
const PostInvite = () => {
  const [error, setError] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Safely call useAuth
  let auth;
  try {
    auth = useAuth();
  } catch (err) {
    console.error(
      `[PostInvite] [${new Date().toISOString()}] useAuth error:`,
      err
    );
    dlog('[PostInvite]', { message: 'useAuth error', error: err });
    setError('Authentication context error. Please try again or log in.');
    return null;
  }
  const { user, loading } = auth;

  useEffect(() => {
    setIsMounted(true);
    dlog('[PostInvite]', {
      message: 'Component mounted',
      user: user?.id,
      loading,
      location: location.pathname + location.search + location.hash,
    });

    const handlePostInvite = async () => {
      if (!isMounted) return;

      try {
        // Parse parameters from hash (Supabase appends auth params here after /verify redirect)
        const hashParams = new URLSearchParams(location.hash.substring(1));
        const hashError = hashParams.get('error');
        const hashErrorCode = hashParams.get('error_code');
        const hashErrorDescription = hashParams.get('error_description');
        const access_token = hashParams.get('access_token');
        const refresh_token = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        // Handle errors from hash (e.g., expired/invalid token)
        if (hashError || hashErrorCode) {
          dlog('[PostInvite]', {
            message: 'Invite link error',
            hashError,
            hashErrorCode,
            hashErrorDescription,
          });
          setError(
            hashErrorDescription ||
              'The invite link is invalid or has expired. Please request a new one.'
          );
          toast({
            variant: 'destructive',
            title: 'Invite Link Invalid',
            description:
              hashErrorDescription ||
              'The invite link is invalid or has expired. Please request a new one.',
          });
          navigate('/login', { replace: true });
          return;
        }

        // If auth tokens are in hash (from successful verification), set the session explicitly
        if (access_token && refresh_token && type === 'invite') {
          dlog('[PostInvite]', {
            message: 'Setting session from hash params',
          });
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token,
            refresh_token,
          });
          if (setSessionError) {
            console.error(
              `[PostInvite] [${new Date().toISOString()}] Set session error:`,
              setSessionError
            );
            dlog('[PostInvite]', {
              message: 'Set session error',
              setSessionError,
            });
            setError(
              'Failed to authenticate. Please try again or request a new invite.'
            );
            toast({
              variant: 'destructive',
              title: 'Authentication Failed',
              description:
                'Failed to authenticate. Please try again or request a new invite.',
            });
            navigate('/login', { replace: true });
            return;
          }
          // After setting session, refresh to get the latest user
          const { data: refreshedSession } = await supabase.auth.getSession();
          if (!refreshedSession.session?.user) {
            throw new Error('No user after setting session');
          }
        }

        if (loading) {
          dlog('[PostInvite]', {
            message: 'Waiting for auth loading',
            loading,
          });
          return;
        }

        if (!user) {
          dlog('[PostInvite]', {
            message: 'No user session, attempting to refresh',
          });
          const { data: sessionData, error } = await Promise.race([
            supabase.auth.getSession(),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Session fetch timeout')), 5000)
            ),
          ]);
          if (error) {
            console.error(
              `[PostInvite] [${new Date().toISOString()}] Session refresh error:`,
              error
            );
            dlog('[PostInvite]', { message: 'Session refresh error', error });
            setError(
              'Unable to verify invite. Please log in or request a new invite.'
            );
            toast({
              variant: 'destructive',
              title: 'Authentication Failed',
              description:
                'Unable to verify invite. Please log in or request a new invite.',
            });
            navigate('/login', { replace: true });
            return;
          }
          if (!sessionData.session?.user) {
            dlog('[PostInvite]', { message: 'No user after session refresh' });
            setError(
              'No user session found. Please log in or request a new invite.'
            );
            toast({
              variant: 'destructive',
              title: 'Authentication Failed',
              description:
                'No user session found. Please log in or request a new invite.',
            });
            navigate('/login', { replace: true });
            return;
          }
          return;
        }

        const metadata = user.user_metadata || {};
        dlog('[PostInvite]', { message: 'User metadata', metadata });

        if (metadata.requires_password) {
          dlog('[PostInvite]', {
            message: 'requires_password=true, redirecting to set-password',
          });
          navigate('/set-password', { replace: true });
        } else {
          dlog('[PostInvite]', {
            message: 'requires_password=false, redirecting to dashboard',
          });
          navigate('/dashboard', { replace: true });
        }
      } catch (err) {
        console.error(
          `[PostInvite] [${new Date().toISOString()}] Unexpected error:`,
          err
        );
        dlog('[PostInvite]', { message: 'Unexpected error', error: err });
        setError(
          'An unexpected error occurred. Please log in or request a new invite.'
        );
        toast({
          variant: 'destructive',
          title: 'Authentication Error',
          description:
            'An unexpected error occurred. Please log in or request a new invite.',
        });
        navigate('/login', { replace: true });
      }
    };

    const timeoutId = setTimeout(() => {
      if (!isMounted) return;
      dlog('[PostInvite]', { message: 'Timeout: Forcing redirect to login' });
      setError(
        'Authentication timeout. Please log in or request a new invite.'
      );
      toast({
        variant: 'destructive',
        title: 'Authentication Timeout',
        description:
          'Unable to process invite. Please log in or request a new invite.',
      });
      navigate('/login', { replace: true });
    }, 5000);

    handlePostInvite();

    return () => {
      setIsMounted(false);
      clearTimeout(timeoutId);
    };
  }, [user, loading, navigate, location.hash]); // Depend on hash instead of search

  if (error) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <p className='text-red-500 text-sm'>{error}</p>
          <Button
            className='mt-4'
            onClick={() => navigate('/login', { replace: true })}
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen flex items-center justify-center'>
      <div className='text-center'>
        <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-primary'></div>
        <p className='mt-4 text-muted-foreground'>Processing invite...</p>
      </div>
    </div>
  );
};

// Error boundary wrapper
class PostInviteErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error(
      `[PostInviteErrorBoundary] [${new Date().toISOString()}] Caught error:`,
      error,
      errorInfo
    );
    dlog('[PostInviteErrorBoundary]', {
      message: 'Caught error',
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className='min-h-screen flex items-center justify-center'>
          <div className='text-center'>
            <p className='text-red-500 text-sm'>
              An error occurred while processing the invite. Please try again or
              log in.
            </p>
            <Button
              className='mt-4'
              onClick={() => window.location.assign('/login')}
            >
              Go to Login
            </Button>
          </div>
        </div>
      );
    }

    return <PostInvite />;
  }
}

export default PostInviteErrorBoundary;
